import React from 'react';
import { LegalLayout } from '@/components/layouts/LegalLayout';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';

export const PolitiqueConfidentialite: React.FC = () => {
  return (
    <LegalLayout>
      <div className="prose prose-slate dark:prose-invert max-w-none">
        <div className="flex items-center gap-3 mb-6">
          <h1 className="text-3xl font-bold">Politique de confidentialité</h1>
          <Badge variant="outline" className="text-xs">RGPD</Badge>
        </div>
        
        <p className="text-muted-foreground mb-8">
          Dernière mise à jour : [Date - À remplir]
        </p>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Responsable du traitement</h2>
          <div className="bg-muted/30 p-4 rounded-lg">
            <p><strong>Société :</strong> [Nom de la société - À remplir]</p>
            <p><strong>Adresse :</strong> [Adresse - À remplir]</p>
            <p><strong>Email :</strong> [dpo@votredomaine.com - À remplir]</p>
          </div>
        </section>

        <Separator className="my-6" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Données collectées</h2>
          
          <div className="space-y-6">
            <div className="border border-border rounded-lg p-4">
              <h3 className="font-semibold mb-3">👤 Données d'identification</h3>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Nom, prénom</li>
                <li>Adresse email</li>
                <li>Numéro de téléphone</li>
                <li>Informations de l'entreprise (nom, SIRET, adresse)</li>
              </ul>
            </div>

            <div className="border border-border rounded-lg p-4">
              <h3 className="font-semibold mb-3">🛒 Données d'utilisation</h3>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Données de stock et produits</li>
                <li>Historique des ventes</li>
                <li>Données de caisse</li>
                <li>Logs de connexion</li>
                <li>Adresse IP</li>
              </ul>
            </div>

            <div className="border border-border rounded-lg p-4">
              <h3 className="font-semibold mb-3">💳 Données de facturation</h3>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Informations de paiement (via Stripe)</li>
                <li>Historique des factures</li>
                <li>Données de facturation</li>
              </ul>
            </div>
          </div>
        </section>

        <Separator className="my-6" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Finalités du traitement</h2>
          <div className="grid gap-4">
            <div className="bg-primary/5 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">🔐 Gestion du compte utilisateur</h3>
              <p className="text-sm">Création, authentification et gestion de votre compte.</p>
            </div>
            
            <div className="bg-primary/5 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">📊 Fonctionnement du service</h3>
              <p className="text-sm">Gestion de stock, caisse, ventes et données commerciales.</p>
            </div>
            
            <div className="bg-primary/5 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">💰 Facturation et paiement</h3>
              <p className="text-sm">Traitement des paiements et émission des factures.</p>
            </div>
            
            <div className="bg-primary/5 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">🛡️ Sécurité</h3>
              <p className="text-sm">Prévention de la fraude et sécurisation des données.</p>
            </div>
          </div>
        </section>

        <Separator className="my-6" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Durée de conservation</h2>
          <div className="bg-muted/30 p-4 rounded-lg">
            <ul className="space-y-2 text-sm">
              <li><strong>Données de compte :</strong> Durée de l'abonnement + 3 ans</li>
              <li><strong>Données commerciales :</strong> 10 ans (obligations comptables)</li>
              <li><strong>Données de facturation :</strong> 10 ans (obligations fiscales)</li>
              <li><strong>Logs de connexion :</strong> 1 an maximum</li>
            </ul>
          </div>
        </section>

        <Separator className="my-6" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Vos droits RGPD</h2>
          <div className="grid gap-3">
            <div className="flex items-start gap-3 p-3 border rounded-lg">
              <span className="text-lg">👁️</span>
              <div>
                <h3 className="font-semibold text-sm">Droit d'accès</h3>
                <p className="text-xs text-muted-foreground">Consulter vos données personnelles</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 border rounded-lg">
              <span className="text-lg">✏️</span>
              <div>
                <h3 className="font-semibold text-sm">Droit de rectification</h3>
                <p className="text-xs text-muted-foreground">Corriger vos données inexactes</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 border rounded-lg">
              <span className="text-lg">🗑️</span>
              <div>
                <h3 className="font-semibold text-sm">Droit à l'effacement</h3>
                <p className="text-xs text-muted-foreground">Supprimer vos données (conditions)</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 border rounded-lg">
              <span className="text-lg">📦</span>
              <div>
                <h3 className="font-semibold text-sm">Droit à la portabilité</h3>
                <p className="text-xs text-muted-foreground">Récupérer vos données</p>
              </div>
            </div>
          </div>
        </section>

        <Separator className="my-6" />

        <section>
          <h2 className="text-xl font-semibold mb-4">Contact</h2>
          <div className="bg-primary/10 p-4 rounded-lg">
            <p className="mb-2">
              <strong>Pour exercer vos droits, contactez-nous :</strong>
            </p>
            <p><strong>Email :</strong> [dpo@votredomaine.com - À remplir]</p>
            <p><strong>Adresse :</strong> [Adresse postale - À remplir]</p>
            <p className="text-sm text-muted-foreground mt-3">
              Nous nous engageons à répondre dans un délai de 30 jours maximum.
            </p>
          </div>
        </section>
      </div>
    </LegalLayout>
  );
};